import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "academic_administrative_role";

const AcademicAdministrativeRole = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    starting_month_year: null,
    ending_month_year: null,
    position: "",
    institution: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = (Array.isArray(data) ? data : [])
      .map((r) => ({
        ...r,
        starting_month_year: r.starting_month_year
          ? new Date(r.starting_month_year)
          : null,
        ending_month_year: r.ending_month_year
          ? new Date(r.ending_month_year)
          : null,
      }))
      .sort((a, b) => {
        if (!a.starting_month_year) return 1;
        if (!b.starting_month_year) return -1;

        // Latest starting date first
        return (
          b.starting_month_year.getTime() -
          a.starting_month_year.getTime()
        );
      });

    setRecords(processed);
  } catch (err) {
    toast.error(`Fetch error: ${err.message}`);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        starting_month_year: formData.starting_month_year
          ? format(formData.starting_month_year, "yyyy-MM") + "-01"
          : null,
        ending_month_year: formData.ending_month_year
          ? format(formData.ending_month_year, "yyyy-MM") + "-01"
          : null,
      };

      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Record saved successfully!");
      setFormData({
        starting_month_year: null,
        ending_month_year: null,
        position: "",
        institution: "",
        country: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (record) => {
    setFormData({
      starting_month_year: record.starting_month_year
        ? new Date(record.starting_month_year)
        : null,
      ending_month_year: record.ending_month_year
        ? new Date(record.ending_month_year)
        : null,
      position: record.position || "",
      institution: record.institution || "",
      country: record.country || "",
    });
    setRecordId(record.id);
    toast.info("Editing record...");
  };

  const handleDelete = (record) => {
    const toastId = toast.info(
      <div>
        Delete this record?{" "}
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${record.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };
const formatMonthYear = (date) => {
  if (!date) return "";

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${year}`;
};
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Academic Administrative Role
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "15px" }}
        >
          {/* Dates Row */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Starting Month/Year (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.starting_month_year}
                onChange={(date) =>
                  setFormData({ ...formData, starting_month_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Ending Month/Year (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.ending_month_year}
                onChange={(date) =>
                  setFormData({ ...formData, ending_month_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Position */}
          <div>
            <label style={{ fontWeight: "bold" }}>Position</label>
            <input
              type="text"
              name="position"
              value={formData.position}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          {/* Institution */}
          <div>
            <label style={{ fontWeight: "bold" }}>Institution</label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          {/* Country */}
          <div>
            <label style={{ fontWeight: "bold" }}>Country</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Start",
                    "End",
                    "Position",
                    "Institution",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
  {formatMonthYear(rec.starting_month_year)}
</td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
  {formatMonthYear(rec.ending_month_year)}
</td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.position}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcademicAdministrativeRole;
